import { z } from 'zod';

export const DEFAULT_LAUNCHER_MX = 24;
export const DEFAULT_LAUNCHER_MY = 24;
/** Max 32-bit signed integer — keeps the embed launcher above typical host-page overlays. */
export const DEFAULT_LAUNCHER_Z_INDEX = 2147483647;

export interface LauncherEmbedStyle {
  mx: number;
  my: number;
  zIndex: number;
}

const offsetSchema = z.number().int().min(1);
const zIndexSchema = z.number().int().min(1);

const DEFAULTS: LauncherEmbedStyle = {
  mx: DEFAULT_LAUNCHER_MX,
  my: DEFAULT_LAUNCHER_MY,
  zIndex: DEFAULT_LAUNCHER_Z_INDEX,
};

function normalizeKey(rawKey: string): 'mx' | 'my' | 'z-index' | null {
  const key = rawKey.trim().toLowerCase();
  if (key === 'mx') return 'mx';
  if (key === 'my') return 'my';
  if (key === 'z-index' || key === 'zindex') return 'z-index';
  return null;
}

function parseNumericValue(rawValue: string): number | null {
  const trimmed = rawValue.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return null;
  return parsed;
}

/**
 * Parse optional embed script `style` attribute values for launcher positioning.
 * Format: "z-index:5000; mx:32; my:48" (semicolon-separated key:value pairs).
 */
export function parseEmbedStyle(styleAttr: string | null | undefined): LauncherEmbedStyle {
  if (!styleAttr?.trim()) {
    return { ...DEFAULTS };
  }

  const resolved: LauncherEmbedStyle = { ...DEFAULTS };

  for (const segment of styleAttr.split(';')) {
    const colonIndex = segment.indexOf(':');
    if (colonIndex === -1) continue;

    const rawKey = segment.slice(0, colonIndex);
    const rawValue = segment.slice(colonIndex + 1);
    const key = normalizeKey(rawKey);
    const numericValue = parseNumericValue(rawValue);
    if (key === null || numericValue === null) continue;

    if (key === 'mx') {
      const result = offsetSchema.safeParse(numericValue);
      if (result.success) resolved.mx = result.data;
    } else if (key === 'my') {
      const result = offsetSchema.safeParse(numericValue);
      if (result.success) resolved.my = result.data;
    } else if (key === 'z-index') {
      const result = zIndexSchema.safeParse(numericValue);
      if (result.success) resolved.zIndex = result.data;
    }
  }

  return resolved;
}
